import React, { createContext, useContext, useMemo } from 'react'
import type { TenantConfig } from '../types/tenant-config'

/**
 * Context for tenant configuration from the Experience API.
 * Provides site/country/locale structure to components for GeoIP bar,
 * language bar, and country selector.
 *
 * Value is null when the Experience API is unavailable — components should
 * fall back to hardcoded VistaPrint data in that case.
 */
const TenantConfigContext = createContext<TenantConfig | null>(null)

export interface TenantConfigProviderProps {
  value: TenantConfig | null
  children: React.ReactNode
}

export const TenantConfigProvider: React.FC<TenantConfigProviderProps> = ({ value, children }) => {
  const memoizedValue = useMemo(() => value, [value])
  return <TenantConfigContext.Provider value={memoizedValue}>{children}</TenantConfigContext.Provider>
}

/**
 * Hook to access the current tenant configuration.
 * Returns null when:
 * - The Experience API was unavailable during SSR
 * - The component is rendered outside a TenantConfigProvider
 *
 * Components should handle null by falling back to hardcoded VistaPrint data.
 */
export const useTenantConfig = (): TenantConfig | null => {
  return useContext(TenantConfigContext)
}

export { TenantConfigContext }
